/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../locales/en-US/descriptors';
import {
  formatEndDate,
  formattedAddOneMoreDate,
  formattedDate,
  isObjectOrArrayAndEmpty,
  returnFormattedDate
} from '../../../shared/Utils/Utils';
import useConnectorAndDynamicProps from '../../../shared/useConnectorAndDynamicProps/useConnectorAndDynamicProps';
import ConnectionError from '../../../shared/ErrorComponents/ConnectionError';
import withLoadingStatus from '../../../shared/StatusWrappers/LoadingStatus';
import withErrorStatus from '../../../shared/StatusWrappers/ErrorStatus';
import {
  IEnergyOptimizationComponentProps,
  IEnergyTableOptimizationWidgetProps
} from '../EnergyOptimizationInterfaces';
import useMounted from '../../../shared/useMount/useMount';
import EnergyOptimizationWidget from './EnergyOptimizationWidget';
import {
  IAny,
  IEmaasEnergyOptimizationComponentData,
  ISiteCard
} from '../../IEnergyManagementProps';
import { ApiContext } from '../../../../context/ApiContext';
import EnergyOptimizationTableWidget from './EnergyOptimizationTableWidget';
import './EnergyOptimizationTableWidgetTheme.scss';
import moment from 'moment';
import { isThisMonth, subDays } from 'date-fns';
interface IEmaasEnergyOptimizationUtilityHOCContent extends ISiteCard {
  data: IEmaasEnergyOptimizationComponentData;
  mainGraph?: boolean;
  startTime?: IAny;
  endTime?: IAny;
  utilityType?: IAny;
  eoOperationData?: IEmaasEnergyOptimizationComponentData;
  dataOp?: IAny;
  toggleEnableEoTable?: boolean;
}

const EnergyOptimizationComponentUtilityWithContent = (
  props: IEmaasEnergyOptimizationUtilityHOCContent
) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const { data, onRefresh, mainGraph } = props;

  if (isObjectOrArrayAndEmpty(data)) {
    return (
      <div className="errorDisplay common-error-container">
        <ConnectionError
          title={t(d.CONNECTIONERRORTITLE)}
          message={t(d.CONNECTIONERROR)}
          onRefresh={onRefresh}
        />
      </div>
    );
  }

  return (
    <>
      <EnergyOptimizationWidget data={data} mainGraph={mainGraph} />
    </>
  );
};

const EnergyOptimizationComponentUtilityWithTableContent = (
  props: IEnergyTableOptimizationWidgetProps
) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();

  if (isObjectOrArrayAndEmpty(props.data)) {
    return (
      <div className="errorDisplay common-error-container">
        <ConnectionError
          title={t(d.CONNECTIONERRORTITLE)}
          message={t(d.CONNECTIONERROR)}
          onRefresh={props.onRefresh}
        />
      </div>
    );
  }

  return (
    <>
      <EnergyOptimizationTableWidget {...props} />
    </>
  );
};

export const EnergyOptimizationUtilityComponentWithLoader = withLoadingStatus(
  withErrorStatus(EnergyOptimizationComponentUtilityWithContent)
);

export const EnergyOptimizationUtilityComponentTableWithLoader = withLoadingStatus(
  withErrorStatus(EnergyOptimizationComponentUtilityWithTableContent)
);

const EnergyOptimizationUtilityComponent = (
  props: IEnergyOptimizationComponentProps
) => {
  const [t] = useTranslation();
  const d = descriptors.energyManagement;
  const {
    bearerToken,
    requestContext,
    eoOperationData,
    setEoOperationData
  } = props;
  const [utilityType, setUtilityType] = useState<string>(props.utilityType!);
  const apiContext = useContext(ApiContext);
  const {
    onEOUtilityTabChange,
    triggerDateChange,
    toggleEnableEoTable
  } = apiContext;

  let startTime = '';
  if (requestContext.context.startTime) {
    startTime = formattedDate(requestContext.context.startTime);
  }
  let endTime = '';
  if (requestContext.context.endTime) {
    endTime = formattedDate(requestContext.context.endTime);
  }
  let endTimeOP = '';
  if (requestContext.context.endTime) {
    endTimeOP = formattedAddOneMoreDate(requestContext.context.endTime);
  }
  const isMounted = useMounted();

  let connectorOPConfig: IAny = {
    name: 'operation-parameter',
    options: {
      startDate: startTime,
      endDate: endTimeOP,
      utility: utilityType
    }
  };

  let connectorConfig: IAny = {
    name: 'emaas-energy-optimization',
    options: {
      startDate: startTime,
      endDate: endTime
    }
  };

  const [dataOPConnector] = useConnectorAndDynamicProps({
    connectorConfig: connectorOPConfig,
    requestContext,
    bearerToken
  });

  const [dataConnector] = useConnectorAndDynamicProps({
    connectorConfig,
    requestContext,
    bearerToken
  });

  const checkIfDataIsThere = (data: IAny) => {
    return (
      data.dataItems.filter((dt: IAny) => dt.name === 'ActualCost')[0].values
        .length > 0
    );
  };

  const getEoDataonTabChange = async (utilityType: string) => {
    setIsLoading(true);
    try {
      if (onEOUtilityTabChange) {
        const apiRes = await onEOUtilityTabChange(utilityType);
        if (!checkIfDataIsThere(apiRes.data)) {
          setIsLoading(false);
          setStatusCode(204);
        } else {
          setStatusCode(apiRes.status);
          setData(apiRes.data);
          setIsLoading(false);
        }
      } else {
        console.log('onEOUtilityTabChange is not defined');
        setStatusCode(500);
      }
    } catch (e) {
      setErrorInfo(e);
      setIsLoading(false);
      setStatusCode(500);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (triggerDateChange) {
      getEoDataonTabChange(utilityType);
    }
  }, [triggerDateChange]);

  useEffect(() => {
    if (isMounted) {
      dataOPConnector?.actions?.getAll();
    }
  }, [requestContext, utilityType]);

  useEffect(() => {
    if (isMounted) {
      dataConnector?.actions?.getAll();
    }
  }, [requestContext.context.startTime, requestContext.context.endTime]);

  const {
    all: allOP,
    loading: loadingOP,
    error: errorOP,
    status: statusOP
  } = dataOPConnector.data;

  const energyOptimizationOP = (allOP as unknown) as IAny;
  const { all, loading, error, status } = dataConnector.data;

  const energyOptimization = (all as unknown) as IEmaasEnergyOptimizationComponentData;
  let isDataAvailable = false;
  if (energyOptimization) {
    isDataAvailable = true;
  }

  let isDataAvailableOP = false;
  if (energyOptimizationOP) {
    isDataAvailableOP = true;
  }

  const [dataOp, setDataOp] = useState({} as IAny);

  useEffect(() => {
    if ((isDataAvailableOP || errorOP || statusOP) && !loadingOP) {
      if (isDataAvailableOP) {
        setDataOp(energyOptimizationOP);
      }
    }
  }, [isDataAvailableOP, loadingOP, statusOP]);

  useEffect(() => {
    if ((isDataAvailable || error || status) && !loading) {
      if (isDataAvailable) {
        setEoOperationData && setEoOperationData(energyOptimization);
      }
      setErrorInfo(error);
      setStatusCode(status);
    }
  }, [isDataAvailable, loading, status]);

  const getEODataByUtility = async (utilityType: string) => {
    setUtilityType(utilityType);
    getEoDataonTabChange(utilityType);
  };

  const onRefresh = async () => {
    await getEODataByUtility(props.utilityType!);
    await dataOPConnector?.actions?.getAll();
  };

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [StatusCode, setStatusCode] = useState<number | null>(null);
  const [errorInfo, setErrorInfo] = useState({} as IAny);
  const [data, setData] = useState({} as IEmaasEnergyOptimizationComponentData);

  const checkIfThisMonthIsSelected = (endTime: string) => {
    const isCurrentMonth = isThisMonth(new Date(endTime.split('T')[0]));
    const end = moment(new Date(endTime)).format('YYYY-MM-DD');
    if (isCurrentMonth) {
      if (end >= moment(new Date()).format('YYYY-MM-DD')) {
        return subDays(new Date(), 1).toISOString();
      }
    }
    return endTime;
  };

  // useEffect(()=> {
  //   if(isMounted) {
  //     props.saveDataInSite && props.saveDataInSite(data)
  //   }
  // },[data])

  // On Component Mount or utilitytype change, call API
  useEffect(() => {
    getEODataByUtility(props.utilityType!);
  }, [isMounted, props.utilityType]);

  return (
    <>
      <EnergyOptimizationUtilityComponentWithLoader
        loading={isLoading}
        error={errorInfo}
        statusCode={StatusCode}
        onRefresh={onRefresh}
        data={data}
        loadingText={t(d.LOADING)}
        title={t(d.CONNECTIONERRORTITLE)}
        noDataMessage={t(d.NODATAAVAILABLE)}
        connectionErrorMessage={t(d.CONNECTIONERROR)}
        mainGraph={props.mainGraph}
        startTime={startTime}
        endTime={endTime}
        utilityType={utilityType}
        eoOperationData={eoOperationData}
        dataOp={dataOp}
        toggleEnableEoTable={toggleEnableEoTable}
      />
      {toggleEnableEoTable && (
        <div className="EOTableData">
          <EnergyOptimizationUtilityComponentTableWithLoader
            loading={isLoading}
            error={errorInfo}
            statusCode={StatusCode}
            onRefresh={onRefresh}
            loadingText={t(d.LOADING)}
            title={t(d.CONNECTIONERRORTITLE)}
            noDataMessage={t(d.NODATAAVAILABLE)}
            connectionErrorMessage={t(d.CONNECTIONERROR)}
            isAllUtility={false}
            utilityType={utilityType}
            data={data}
            startTime={startTime}
            endTime={checkIfThisMonthIsSelected(endTime)}
            eoOperationData={eoOperationData}
            dataOp={dataOp}
            isLoading={!isLoading}
          />
        </div>
      )}
    </>
  );
};
export default EnergyOptimizationUtilityComponent;
